import { useEffect, useState } from "preact/hooks";
import type { Article, Tag } from "../lib/types";
import { listTags, ensureTag, deleteTag, listArticles, archiveArticle, favoriteArticle, tagsForArticle } from "../lib/db";
import { navigate } from "../lib/router";
import { ArticleList } from "../components/ArticleList";

export function Tags({ params }: { params: Record<string, string> }) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      setTags(await listTags());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const addTag = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await ensureTag(name);
      setNewName("");
      await reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const removeTag = async (id: string, name: string) => {
    if (!confirm(`Delete tag "${name}"? It will be removed from all articles.`)) return;
    await deleteTag(id);
    await reload();
  };

  if (params.name) {
    return (
      <>
        <div class="topbar">
          <h1>#{decodeURIComponent(params.name)}</h1>
          <div class="grow" />
          <button class="ghost" onClick={() => navigate("/tags")}>All tags</button>
        </div>
        <TagArticlesView name={decodeURIComponent(params.name)} />
      </>
    );
  }

  return (
    <>
      <div class="topbar">
        <h1>Tags</h1>
      </div>
      {error && <div class="banner error">{error}</div>}

      <form class="row" onSubmit={(e) => { e.preventDefault(); void addTag(); }} style="margin-bottom: var(--space-5);">
        <input type="text" placeholder="New tag name" value={newName}
          onInput={(e) => setNewName((e.target as HTMLInputElement).value)} />
        <button class="primary" type="submit">Add</button>
      </form>

      {loading ? (
        <div class="empty"><div class="spinner" /></div>
      ) : tags.length === 0 ? (
        <div class="empty">
          <h2>No tags yet</h2>
          <p class="muted">Tags organize articles without folders.</p>
        </div>
      ) : (
        <div class="col">
          {tags.map((t) => (
            <div class="list-item" key={t.id} style="grid-template-columns: 1fr auto;">
              <a href={`#/tags/${encodeURIComponent(t.name)}`} style="text-decoration: none;">
                <div class="title">#{t.name}</div>
              </a>
              <button class="icon ghost" onClick={() => removeTag(t.id, t.name)} aria-label={`Delete tag ${t.name}`}>×</button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function TagArticlesView({ name }: { name: string }) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const all = await listArticles({ filter: "inbox", sort: "saved" });
        const filtered: Article[] = [];
        for (const a of all) {
          const tags = await tagsForArticle(a.id);
          if (tags.some((t) => t.name.toLowerCase() === name.toLowerCase())) filtered.push(a);
        }
        setArticles(filtered);
      } finally {
        setLoading(false);
      }
    })();
  }, [name]);

  if (loading) return <div class="empty"><div class="spinner" /></div>;
  if (articles.length === 0) return <div class="empty"><h2>No articles with this tag</h2></div>;

  return (
    <ArticleList
      articles={articles}
      selected={new Set<string>()}
      onToggleSelect={() => {}}
      onArchive={async (id) => { await archiveArticle(id, true); setArticles((p) => p.filter((a) => a.id !== id)); }}
      onFavorite={async (id, v) => { await favoriteArticle(id, v); setArticles((p) => p.map((a) => a.id === id ? { ...a, favorite: v } : a)); }}
      onOpen={(a) => navigate(`/articles/${a.id}`)}
    />
  );
}
